// src/config/sentry.js
// Phase 29 OBS-01 — Sentry browser SDK configuration.
//
// Scope: errors-only (D-01). No performance tracing, no session replay.
// Gates: production + QNAP staging + Vercel staging; never Vite dev (Phase 29 D-02, Phase 30 D-02); suppressed
//        whenever localStorage.tend:devmode === 'true' (D-04).
// PII:   sendDefaultPii: false, plus a beforeSend hook that redacts UK
//        postcodes and Anthropic API key fragments, and strips
//        tend:apikey / tend:location leak paths (D-03).
//
// Public DSN — safe to hardcode. Rate-limited server-side by Sentry.
import * as Sentry from '@sentry/react';

export const SENTRY_DSN = 'https://a3b147e30b77cef5201de40cc4bd702b@o4511241883025408.ingest.de.sentry.io/4511241885974608';

// Covers current-generation user keys (api03), legacy user keys (api01/api02),
// and admin keys (admin01+). If Anthropic ever adds api04+ or admin02+ the
// pattern still matches without a code change.
const ANTHROPIC_KEY_REGEX = /sk-ant-(?:api0[1-9]|admin0[1-9])-[A-Za-z0-9_-]+/g;
const UK_POSTCODE_REGEX   = /\b[A-Z]{1,2}\d[A-Z\d]? ?\d[A-Z]{2}\b/gi;
// Phase 40 (CONTEXT D-09 + threat T-40-01-04) — Supabase JWTs and Bearer
// header values must never reach Sentry. The JWT regex matches the standard
// three-segment compact JWS shape; the Bearer regex catches the header form
// even when the token is otherwise opaque.
const SUPABASE_JWT_REGEX  = /eyJ[A-Za-z0-9_-]+\.eyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+/g;
const BEARER_HEADER_REGEX = /Bearer\s+[A-Za-z0-9._-]+/gi;

function safeGetItem(key) {
  try { return typeof localStorage !== 'undefined' ? localStorage.getItem(key) : null; }
  catch { return null; }
}

/**
 * Redact known sensitive patterns from a single string.
 * Exported for unit testing.
 */
export function redactString(s) {
  if (typeof s !== 'string') return s;
  return s
    .replace(ANTHROPIC_KEY_REGEX, '[REDACTED:anthropic-key]')
    .replace(UK_POSTCODE_REGEX,   '[REDACTED:postcode]')
    // Bearer header form FIRST so the inner token is rewritten via the
    // wrapper, then the standalone-JWT pass cleans anything still bare.
    .replace(BEARER_HEADER_REGEX, 'Bearer [REDACTED:jwt]')
    .replace(SUPABASE_JWT_REGEX,  '[REDACTED:jwt]');
}

/**
 * Recursively strip values whose keys begin with `sb-` (Supabase Auth
 * default-storage convention — `sb-<project-ref>-auth-token` etc.) from any
 * object. Used by beforeSend to scrub localStorage shadows that may surface
 * via event.extra or breadcrumb.data. Mutates in place; returns the input
 * for chaining.
 */
function stripSbKeys(obj) {
  if (!obj || typeof obj !== 'object') return obj;
  for (const k of Object.keys(obj)) {
    if (k.startsWith('sb-')) {
      obj[k] = '[REDACTED:sb-key]';
    } else if (typeof obj[k] === 'object') {
      stripSbKeys(obj[k]);
    }
  }
  return obj;
}

/**
 * Last-chance scrubber before an event leaves the browser.
 * Returns null to drop the event entirely (dev-mode suppression).
 */
export function beforeSend(event /*, hint */) {
  // D-04 belt-and-braces: if dev-mode was toggled ON mid-session, drop
  // the event even though initSentry() already refused to run in a fresh
  // dev-mode session.
  if (safeGetItem('tend:devmode') === 'true') return null;

  if (event.message) event.message = redactString(event.message);
  if (event.exception?.values) {
    for (const v of event.exception.values) {
      if (v.value) v.value = redactString(v.value);
    }
  }
  if (event.request?.url)          event.request.url          = redactString(event.request.url);
  if (event.request?.query_string) event.request.query_string = redactString(event.request.query_string);

  if (event.extra) {
    for (const k of Object.keys(event.extra)) {
      if (typeof event.extra[k] === 'string') {
        event.extra[k] = redactString(event.extra[k]);
      }
    }
  }

  // Phase 40 (T-40-01-04) — strip sb-* localStorage shadows from extras +
  // breadcrumb data. The values would otherwise leak Supabase auth tokens.
  if (event.extra) stripSbKeys(event.extra);
  if (event.breadcrumbs) {
    for (const b of event.breadcrumbs) {
      if (b?.data) stripSbKeys(b.data);
    }
  }

  // Never ship user identity fields. We don't set them ourselves but
  // defend against a future code path that does.
  if (event.user) {
    delete event.user.email;
    delete event.user.ip_address;
    delete event.user.username;
  }

  return event;
}

/**
 * Drop sensitive data from breadcrumbs (fetch/xhr bodies to proxy.php
 * may contain Ask Tend prompts or plant notes).
 */
export function beforeBreadcrumb(breadcrumb /*, hint */) {
  if ((breadcrumb.category === 'fetch' || breadcrumb.category === 'xhr')
      && breadcrumb.data?.url?.includes('proxy.php')) {
    delete breadcrumb.data.request_body_size;
    if (breadcrumb.data.url) {
      breadcrumb.data.url = breadcrumb.data.url.split('?')[0];
    }
  }

  if (breadcrumb.message) {
    breadcrumb.message = breadcrumb.message
      .replace(ANTHROPIC_KEY_REGEX, '[REDACTED]')
      .replace(UK_POSTCODE_REGEX, '[REDACTED]');
  }

  return breadcrumb;
}

/**
 * Call once, before React renders. No-op in Vite dev, and no-op when
 * the user has toggled Developer Mode on in production/staging.
 * Wrapped in try/catch — a bad DSN or SDK failure must never break
 * app startup.
 */
export function initSentry() {
  // D-02 — never from Vite dev
  if (import.meta.env.DEV) return;
  // D-04 — never when dev-mode is active
  if (safeGetItem('tend:devmode') === 'true') return;

  try {
    Sentry.init({
      dsn: SENTRY_DSN,
      environment: (() => {
        // Phase 30 D-02 — three distinct environments:
        //   - staging-vercel: VITE_APP_TITLE === 'Tend - Vercel Staging'  (Vercel host)
        //   - staging:        VITE_APP_TITLE === 'Tend - Staging'         (QNAP staging path)
        //   - production:     everything else (QNAP prod path)
        const title = import.meta.env.VITE_APP_TITLE;
        if (title === 'Tend - Vercel Staging') return 'staging-vercel';
        if (title === 'Tend - Staging')        return 'staging';
        return 'production';
      })(),
      release: typeof __GIT_HASH__ !== 'undefined' ? __GIT_HASH__ : 'unknown',
      sendDefaultPii: false,
      // No integrations override → default set (globalHandlers, dedupe,
      //   inboundFilters, breadcrumbs, httpContext, browserSession) applies.
      // No performance sample rate set → performance monitoring stays off
      //   even if a future dep tries to enable it.
      // No replay sample rate set → session replay stays off.
      beforeSend,
      beforeBreadcrumb,
    });
  } catch (e) {
    // Never let Sentry itself take the app down.
    console.warn('Sentry init failed:', e);
  }
}
